import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaPlayCircle,
  FaLock,
  FaUsers,
  FaGraduationCap,
  FaTimesCircle,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../core/common/header/PageHeader";

type Batch = {
  id: any;
  name?: string;
  batchName?: string;
  mentorName?: string;
  zoomLink?: string;
  topic?: string;
  startingDate?: string;
};

interface ActivityStats {
  homeworkSubmitted: number;
  homeworkTotal: number;
  quizSubmitted: number;
  quizTotal: number;
  writingSubmitted: number;
  writingTotal: number;
  classTestSubmitted: number;
  classTestTotal: number;
  attendancePresent: number;
  attendanceTotal: number;
}

type StatColor = "good" | "warn" | "bad";
type StatKind = "attendance" | "classTest" | "default";

// Color by how many were MISSED, not raw percent — tolerance differs per category:
// homework/quiz/writing tolerate 1 miss before it goes amber, 2+ is red; class tests
// have zero slack (1 miss is already red); attendance tolerates 2 misses before amber.
const statColor = (submitted: number, total: number, kind: StatKind): StatColor => {
  const missed = total - submitted;
  if (kind === "attendance") {
    if (missed <= 1) return "good";
    if (missed === 2) return "warn";
    return "bad";
  }
  if (kind === "classTest") {
    return missed === 0 ? "good" : "bad";
  }
  if (missed === 0) return "good";
  if (missed === 1) return "warn";
  return "bad";
};

const COLOR_CLASSES: Record<StatColor, { bar: string; text: string }> = {
  good: { bar: "bg-emerald-500", text: "text-emerald-600" },
  warn: { bar: "bg-amber-500", text: "text-amber-600" },
  bad: { bar: "bg-rose-500", text: "text-rose-600" },
};

const StatBar: React.FC<{
  label: string;
  submitted: number;
  total: number;
  kind: StatKind;
}> = ({ label, submitted, total, kind }) => {
  if (total <= 0) return null;
  const missed = total - submitted;
  const color = statColor(submitted, total, kind);
  const pct = Math.min(100, Math.round((submitted / total) * 100));

  return (
    <div className="border border-gray-200 rounded-lg p-3.5 bg-white">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-semibold text-gray-800">{label}</span>
        <span className="text-xs text-gray-400 tabular-nums">
          {submitted} / {total}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className={`h-full rounded-full ${COLOR_CLASSES[color].bar}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className={`text-xs font-semibold mt-1.5 text-right ${COLOR_CLASSES[color].text}`}>
        {missed === 0 ? "All caught up" : `${missed} missed`}
      </div>
    </div>
  );
};

const mentorInitials = (name?: string) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
};

const StudentBatch: React.FC = () => {
  const { student } = useAuth();

  const isInactive = useMemo(() => {
    const s: any = student || {};
    if (typeof s.isActiveStudent === "boolean")
      return s.isActiveStudent === false;
    if (typeof s.is_active_student === "boolean")
      return s.is_active_student === false;
    return false;
  }, [student]);

  const [batch, setBatch] = useState<Batch | null>(null);
  const [activity, setActivity] = useState<ActivityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joiningClass, setJoiningClass] = useState(false);

  useEffect(() => {
    if (isInactive) {
      setLoading(false);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [bRes, aRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/student/my-batch`, {
            credentials: "include",
          }),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/student/my-activity`, {
            credentials: "include",
          }),
        ]);

        if (bRes.ok) {
          const data = await bRes.json().catch(() => null);
          setBatch(data ? { ...data, id: data.id ?? data.batchId } : null);
        } else {
          setBatch(null);
        }

        if (aRes.ok) {
          const data = await aRes.json().catch(() => null);
          setActivity(data);
        } else {
          setActivity(null);
        }
      } catch (e: any) {
        setError(e.message || "Failed to load data");
        setBatch(null);
        setActivity(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isInactive]);

  const handleJoinClass = async (b: Batch) => {
    if (!b.zoomLink || !student?.studentId || !b.id) {
      alert("Missing Zoom link, batch, or student information.");
      return;
    }

    if (joiningClass) return;

    try {
      setJoiningClass(true);

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/attendance/join?batchId=${b.id}&userId=${student.studentId}&role=STUDENT`,
        { method: "POST", credentials: "include" },
      );

      if (!res.ok) {
        const errorText = await res.text();
        if (
          res.status === 400 ||
          res.status === 500 ||
          errorText.includes("Mentor has not started")
        ) {
          alert(
            "❗ The class has not been started by the mentor yet. Please try again later.",
          );
          return;
        } else {
          throw new Error("Something went wrong while recording attendance.");
        }
      }

      window.open(b.zoomLink, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("Error joining class:", err);
      alert("Failed to join class. Please try again later.");
    } finally {
      setTimeout(() => {
        setJoiningClass(false);
      }, 2000);
    }
  };

  // ---------- Inactive lock screen ----------
  if (isInactive) {
    return (
      <div className="min-h-screen bg-gray-100">
        <PageHeader
          icon={<FaGraduationCap />}
          title="My Batch"
          breadcrumb={[{ label: "Home", to: "/" }, { label: "My Batch" }]}
        />

        <main className="max-w-3xl mx-auto px-6 py-10">
          <div className="border border-rose-200 bg-gradient-to-r from-rose-50 to-pink-50 text-rose-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <FaLock className="mt-0.5" />
              <div>
                <h2 className="font-semibold mb-1">Access Locked</h2>
                <p className="text-sm">
                  Your account is currently inactive. You can't access My Batch.
                  Please clear dues to reactivate your account.
                </p>
                <div className="mt-4 flex gap-3">
                  <Link
                    to="/student/student-dashboard"
                    className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 text-sm transition-colors"
                  >
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const batchLevel = batch?.topic;
  const batchName = batch?.name || batch?.batchName || "Batch Name Not Available";
  const sinceLabel = batch?.startingDate
    ? new Date(batch.startingDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

  // ---------- Normal screen ----------
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaGraduationCap />}
        title="My Batch"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "My Batch" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome Section */}
        <div className="mb-10 bg-gradient-to-r from-[#009fe3] to-cyan-500 text-white rounded-xl p-6 shadow-lg">
          <h2 className="text-3xl font-bold mb-2 text-white flex items-center gap-2">
            <FaUsers />
            {student?.firstName
              ? `Welcome, ${student.firstName} 👋`
              : "Welcome 👋"}
          </h2>
          <p className="text-white opacity-90 text-base">
            Here you can see your batch details, join classes, and track your
            progress.
          </p>
        </div>

        {/* Loading/Error States */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009fe3] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your information...</p>
          </div>
        ) : error ? (
          <div className="bg-gradient-to-r from-rose-50 to-red-50 border border-rose-200 text-rose-800 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-2">
              <FaTimesCircle className="text-rose-600" />
              <h3 className="font-semibold">Error Loading Data</h3>
            </div>
            <p className="text-sm">{error}</p>
          </div>
        ) : (
          <>
            {/* Batch banner */}
            {batch ? (
              <div className="relative overflow-hidden bg-gradient-to-br from-[#e6f6fd] to-white border border-gray-200 rounded-2xl shadow-sm p-5 sm:p-6 mb-8">
                <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#009fe3] to-[#0077b3]" />
                <div className="flex flex-col md:flex-row items-center gap-5">
                  <div className="flex-1 flex flex-col sm:flex-row items-center gap-4 w-full">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#009fe3] to-[#0077b3] flex items-center justify-center flex-shrink-0 shadow-md shadow-[#009fe3]/30">
                      <FaGraduationCap className="text-white text-lg" />
                    </div>
                    <div className="text-center sm:text-left">
                      <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                        <span className="text-base font-extrabold text-gray-900 tracking-tight">
                          {batchName}
                        </span>
                        {batchLevel && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#e6f6fd] text-[#0077b3] border border-gray-200 uppercase tracking-wide">
                            {batchLevel}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-center sm:justify-start flex-wrap gap-x-2.5 gap-y-1 mt-1.5 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="w-[18px] h-[18px] rounded-full bg-gray-100 text-gray-700 text-[9px] font-bold flex items-center justify-center flex-shrink-0">
                            {mentorInitials(batch.mentorName)}
                          </span>
                          {batch.mentorName || "Mentor Not Assigned"}
                        </span>
                        {sinceLabel && (
                          <>
                            <span className="w-[3px] h-[3px] rounded-full bg-gray-300" />
                            <span className="inline-flex items-center gap-1.5">
                              <span className="relative w-[7px] h-[7px] flex-shrink-0">
                                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping" />
                                <span className="absolute inset-0 rounded-full bg-emerald-500" />
                              </span>
                              Since {sinceLabel} · ongoing
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <button
                      onClick={() => handleJoinClass(batch)}
                      disabled={joiningClass}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm shadow-md transition-all ${
                        joiningClass
                          ? "bg-gray-400 cursor-not-allowed text-gray-200"
                          : "bg-emerald-600 text-white hover:bg-emerald-700 hover:-translate-y-0.5 hover:shadow-lg shadow-emerald-600/30"
                      }`}
                    >
                      {joiningClass ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Joining...
                        </>
                      ) : (
                        <>
                          <FaPlayCircle />
                          Join Class
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 text-amber-800 rounded-xl p-6 shadow-sm text-center mb-8">
                <FaUsers className="mx-auto h-12 w-12 text-amber-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  No Batch Assigned
                </h3>
                <p className="text-sm">
                  You haven't been assigned to a batch yet. Please contact
                  support if you think this is an error.
                </p>
              </div>
            )}

            {/* Progress */}
            {activity && (
              <div className="mb-8">
                <h2 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">
                  Your Progress
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <StatBar
                    label="Attendance"
                    submitted={activity.attendancePresent}
                    total={activity.attendanceTotal}
                    kind="attendance"
                  />
                  <StatBar
                    label="Homework"
                    submitted={activity.homeworkSubmitted}
                    total={activity.homeworkTotal}
                    kind="default"
                  />
                  <StatBar
                    label="Quizzes"
                    submitted={activity.quizSubmitted}
                    total={activity.quizTotal}
                    kind="default"
                  />
                  <StatBar
                    label="Writing Tasks"
                    submitted={activity.writingSubmitted}
                    total={activity.writingTotal}
                    kind="default"
                  />
                  <StatBar
                    label="Class Tests"
                    submitted={activity.classTestSubmitted}
                    total={activity.classTestTotal}
                    kind="classTest"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default StudentBatch;
